import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { errorToast } from 'configuration/notifications';
import { clientApi } from 'helpers/api/client';

import { Stock } from 'store/stock/actions';
import { RentExit as RentExitActionService, Rent } from 'store/transferAct/actions';
import { rentInitalSelector, rentExitSelector } from 'store/transferAct/selectors';
import { stockListSelector, stockSelector } from 'store/stock/selectors';

import { Header } from 'components/Header';
import { ButtonIcon } from 'components/Button';
import { Loader } from 'components/Loader';

import { ModalAttention } from 'components/ModalAttention';
import { ActionButtonsSection } from './components/ActionButtonSection';
import { RentExitTable } from './components/RentExitTable';
import { InfoCardsExitSection } from './components/InfoCardsExitSection';
import './style.scss';

const RentExit = () => {
  const { rentId } = useParams();

  const history = useHistory();
  const dispatch = useDispatch();

  const rent = useSelector(rentExitSelector);
  const stock = useSelector(stockSelector);
  const stockList = useSelector(stockListSelector);
  const initalRent = useSelector(rentInitalSelector);

  const [historyModal, setHistoryModal] = useState(null);
  const [attentionModal, setAttentionModal] = useState(false);
  const [isFullPledge, setIsFullPledge] = useState(false);

  useEffect(() => {
    if (!rent) {
      dispatch(RentExitActionService.getRent(rentId));
    }
    dispatch(Rent.getRent(rentId));
  }, []);

  useEffect(() => {
    if (rent?.actID1C && stockList) {
      const findStock = stockList.find((item) => item.stockID1C === rent.stockID);

      if (!findStock) {
        errorToast('У Вас нет прав для редактирования этого склада!');
        history.push('/');
        return;
      }

      if (stock?.stockID1C !== findStock?.stockID1C) {
        dispatch(Stock.setStock(findStock));
      }
    }
  }, [rent]);

  const itemsRefoundAmountSum = useCallback(() => {
    if (!rent?.instruments) {
      return 0;
    }
    let sum =
      rent.instruments.reduce((acc, cur) => {
        let result = acc;
        if (['Послуга', 'Послуги', 'Видаткові матеріали для оренди'].includes(cur.subtype)) {
          return result + cur.itemRentSum * cur.quantityOUT;
        }
        return result + cur.pledge * cur.quantityOUT;
      }, 0) + rent.discount.reduce((acc, n) => acc + Number(n.sum), 0);
    return sum >= 0 ? sum : 0;
  }, [rent]);

  const itemsSurchargeAmountSum = useCallback(() => {
    if (!rent?.instruments) {
      return 0;
    }
    let sum = rent.instruments.reduce((acc, cur) => acc + cur.surcharge_amount * cur.quantity, 0);
    return sum + rent.fine.reduce((acc, n) => acc + Number(n.sum), 0);
  }, [rent]);

  const updateRent = (data) => {
    dispatch(RentExitActionService.updateRent(data));
  };

  const handleBack = () => {
    dispatch(RentExitActionService.clearRent());
    history.push(`/rent/${rentId}`);
  };

  const handleClose = (isSubmit) => {
    if (!isSubmit && JSON.stringify(initalRent) !== JSON.stringify(rent)) {
      setAttentionModal(true);
      return;
    }
    dispatch(RentExitActionService.clearRent());
    toggleAttentionModal(false);
    history.push('/');
  };

  const toggleAttentionModal = (toggle) => {
    setAttentionModal(toggle);
  };

  const handleSave = async () => {
    const client = rent.client && (await clientApi.getClient(rent.client?.id));
    if (client.isTemporary && !isFullPledge) {
      return errorToast('Клиент не полностью заполнен. Отправка данных в 1C невозможна');
    }
    history.push(`/rent/${rentId}/checkout/exit`);
  };

  const handleClickOutside = () => {
    if (historyModal) {
      setHistoryModal(null);
    }
  };

  const handleUpdateTable = (instrumentId, newData) => {
    const findIndex = rent.instruments.findIndex((inst) => inst.id === instrumentId);

    if (findIndex !== -1) {
      const newArray = rent.instruments.slice();

      newArray[findIndex] = newData;
      updateRent({ instruments: newArray });
    }
  };

  const handleRemoveTable = (instrumentId) => {
    const findIndex = rent.instruments.findIndex((inst) => inst.id === instrumentId);

    if (findIndex !== -1) {
      const newArray = rent.instruments.slice();
      newArray.splice(findIndex, 1);

      updateRent({ instruments: newArray });
    }
  };

  if (!rent) {
    return <Loader />;
  }

  return (
    <>
      <Header
        title={`Возврат оборудования ${moment(rent.endDateTime * 1000).format('HH:mm DD.MM')}`}
        onClose={() => handleClose(false)}
      />

      <main className="rent-main" onClick={handleClickOutside}>
        <ActionButtonsSection />

        <RentExitTable
          rateVAT={rent?.agr?.organization?.rateVAT || 0}
          canChange={true}
          instrumentsList={rent.instruments}
          onUpdate={handleUpdateTable}
          onRemove={handleRemoveTable}
          isFullPledge={isFullPledge}
          setIsFullPledge={setIsFullPledge}
        />

        <InfoCardsExitSection rent={rent} stock={stock} showEmpty={true} />

        <div className="rent-maintext">
          {itemsRefoundAmountSum() - itemsSurchargeAmountSum() < 0 && (
            <>
              Сумма доплаты: <b>{itemsSurchargeAmountSum() - itemsRefoundAmountSum()} грн.</b>{' '}
            </>
          )}
          {itemsRefoundAmountSum() - itemsSurchargeAmountSum() > 0 && (
            <>
              Сумма возврата: <b>{itemsRefoundAmountSum() - itemsSurchargeAmountSum()} грн.</b>
            </>
          )}
        </div>

        <div className="buttons-block-grid">
          <div style={{ display: 'flex' }}>
            <ButtonIcon className="btn-main" onClick={handleBack} icon="arrow-left" text="Вернуться к аренде" />
          </div>
          <div className="flex-center">
            <ButtonIcon className="btn-main btn-save" onClick={handleSave} icon="save" text="Продолжить" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ButtonIcon className="btn-main" icon="coin" text="Кассовые ордера" onClick={handleSave} />
          </div>
        </div>
      </main>

      {attentionModal && (
        <ModalAttention onClose={() => toggleAttentionModal(false)} onSubmit={() => handleClose(true)} />
      )}
    </>
  );
};

export { RentExit };
